/* eslint-disable react/jsx-no-target-blank */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import useDocuments from '../../hooks/useDocuments';
import usePlaceholderItems from '../../hooks/usePlaceholderItems';

import { useSite } from '../../contexts/SiteContext';
import AvatarButton from '../buttons/AvatarButton';
import BasicButton from '../buttons/BasicButton';
import DocumentHorizontalCard from '../cards/DocumentHorizontalCard';
import SubscriptionForm from '../forms/SubscriptionForm';

import styles from '../../styles/panels/account-panel.module.css';

import urbaniaAdvantage from '../../assets/img/advantages/urbania.png';

interface AccountPanelProps {
    className?: string;
}

function AccountPanel({ className = null }: AccountPanelProps) {
    const { subscriptions: allSubscriptions = [] } = useSite();
    const { firstname = null, subscriptions: userSubscriptions = [] } = useUser() || {};
    const url = useUrlGenerator();

    const userSubscriptionsIds = (userSubscriptions || [])
        .filter(({ subscribed = false }) => subscribed)
        .map(({ id }) => id);
    const otherSubscriptions = allSubscriptions
        .filter(
            ({ handle, type }) =>
                type !== 'notification' &&
                type !== 'external' &&
                userSubscriptionsIds.indexOf(handle) === -1,
        )
        .map(({ handle }) => handle);

    const { items: lastSubscriptions = null } = useDocuments({
        type: 'newsletter',
        last_from_subscription:
            userSubscriptionsIds.length > 0
                ? [...userSubscriptionsIds, ...otherSubscriptions]
                : otherSubscriptions,
    });

    const placeholderItems = usePlaceholderItems(5);

    // const subscribed = subscriptions.filter(({ subscribed = false }) => subscribed);

    const advantages = [
        {
            label: 'Votre code rabais pour la boutique URBANIA pour obtenir <strong>10% sur tout.</strong>',
            code: 'CESTCADEAU10',
            image: urbaniaAdvantage,
            link: 'https://boutique.urbania.ca/discount/CESTCADEAU10',
        },
        // {
        //     label: 'Votre code rabais pour la boutique URBANIA pour obtenir <strong>la livraison gratuite</strong>',
        //     code: 'URB2025',
        //     image: urbaniaAdvantage,
        // },
    ];

    return (
        <div className={classNames([styles.container, className])}>
            <div className={styles.header}>
                <AvatarButton className={styles.avatar} />
                <div className={styles.image} />
                <h2 className={styles.title}>
                    {firstname !== null ? (
                        <FormattedMessage
                            defaultMessage="Salut {name},"
                            description="Account welcome"
                            values={{ name: firstname }}
                        />
                    ) : (
                        <FormattedMessage defaultMessage="Salut," description="Account welcome" />
                    )}
                </h2>
                <div className={styles.description}>
                    <p>
                        Voici un petit tour d’horizon des dernières infolettres que vous avez
                        reçues, des contenus qui ont capté votre attention sur notre site, et des
                        avantages exclusifs dont vous bénéficiez en tant que membre.
                    </p>
                </div>
            </div>

            <div className={styles.cards}>
                <div className={styles.card}>
                    <div className={styles.top}>Mes abonnements</div>
                    <div className={styles.middle}>
                        {(lastSubscriptions || placeholderItems).map((subscriptionDocument) => {
                            const { id, subscription: documentSubscription } =
                                subscriptionDocument || {};
                            const { handle, image } =
                                allSubscriptions.find(
                                    ({ handle: subscriptionHandle }) =>
                                        subscriptionHandle === documentSubscription,
                                ) || {};
                            const subscribed = userSubscriptionsIds.indexOf(handle) !== -1;
                            return (
                                <div
                                    className={styles.row}
                                    key={`subscription-${id}`}
                                    data-brand={handle}
                                >
                                    <DocumentHorizontalCard
                                        {...subscriptionDocument}
                                        ratio="square"
                                        image={image}
                                        key={handle}
                                        withoutTypeIcon
                                        withoutBrandIcon
                                        withoutTime
                                        className={styles.newsletter}
                                    />
                                    {!subscribed && handle !== null ? (
                                        <SubscriptionForm
                                            subscription={documentSubscription}
                                            buttonOnly
                                            compact
                                            className={styles.button}
                                        />
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    <BasicButton href={url('account.subscriptions')} className={styles.bottom}>
                        <FormattedMessage
                            defaultMessage="Gérer mes abonnements"
                            description="Button label"
                        />
                    </BasicButton>
                </div>

                <div className={styles.card}>
                    <div className={styles.top}>Mes avantages</div>
                    <div className={styles.middle}>
                        {advantages.map(({ label, image, code, link = null }, index) => (
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener"
                                className={styles.advantage}
                                key={`advantage-${index}`}
                            >
                                <div className={styles.content}>
                                    <div
                                        className={styles.label}
                                        dangerouslySetInnerHTML={{ __html: label }}
                                    />
                                    <div className={styles.code}>
                                        {code}
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                            className={styles.icon}
                                        />
                                    </div>
                                </div>
                                <div className={styles.thumbnail}>
                                    <img src={image} className={styles.image} />
                                </div>
                            </a>
                        ))}
                    </div>
                    <div className={styles.inner}></div>
                </div>
            </div>
        </div>
    );
}

export default AccountPanel;
